import React, { type ReactNode } from 'react';
import { LogOut, ShieldCheck } from 'lucide-react';
import { TotpCodePanel, TotpEnrolPanel } from '@/components/security/TwoStepPanels';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ViewLoadingFallback } from '@/components/ui/ViewLoadingFallback';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { supabase } from '@/integrations/supabase/client';
import { useTotpFactors, useWorkspaceMfaStatus } from '@/hooks/useTwoStepVerification';
import { log } from '@/lib/logger';

export type MfaGateState = 'open' | 'checking' | 'verify' | 'enrol' | 'error';

/** Pure decision, so every state is unit-tested without a browser. */
export function mfaGateState(input: {
  required: boolean;
  statusLoading: boolean;
  statusError: boolean;
  satisfied: boolean | undefined;
  factorsLoading: boolean;
  factorsError: boolean;
  verifiedFactorCount: number;
}): MfaGateState {
  if (!input.required) return 'open';
  if (input.statusLoading) return 'checking';
  if (input.statusError) return 'error';
  if (input.satisfied) return 'open';
  if (input.factorsLoading) return 'checking';
  if (input.factorsError) return 'error';
  return input.verifiedFactorCount > 0 ? 'verify' : 'enrol';
}

/**
 * Keeps a workspace's screens closed until the session meets the workspace's
 * two-step verification requirement. The database enforces the same rule on
 * the core record tables, record RPCs, member administration, the audit trail
 * and exports; this gate makes the app ask for the code instead of showing
 * empty screens.
 */
export function WorkspaceMfaGate({ children }: { children: ReactNode }) {
  const { workspace, workspaces, accountUserId, setActiveWorkspace } = useWorkspace();
  const required = workspace?.require_mfa === true;
  const status = useWorkspaceMfaStatus(workspace?.id, required);
  const needsFactors = required && status.data?.satisfied === false;
  const factors = useTotpFactors(needsFactors ? accountUserId : null);
  const verified = (factors.data ?? []).filter(f => f.status === 'verified');

  const state = mfaGateState({
    required,
    statusLoading: status.isLoading,
    statusError: !!status.error,
    satisfied: status.data?.satisfied,
    factorsLoading: factors.isLoading,
    factorsError: !!factors.error,
    verifiedFactorCount: verified.length,
  });

  if (state === 'open') return <>{children}</>;
  if (state === 'checking') return <ViewLoadingFallback />;

  const otherWorkspaces = workspaces.filter(option => option.workspace_id !== workspace?.id);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-lg p-6">
        <h1 className="mb-1 flex items-center gap-2 text-lg font-semibold">
          <ShieldCheck className="h-5 w-5" aria-hidden="true" />
          {state === 'verify' ? 'Enter your verification code' : 'Set up two-step verification'}
        </h1>
        <p className="mb-5 text-sm text-muted-foreground">
          {workspace?.name ?? 'This workspace'} requires two-step verification.{' '}
          {state === 'enrol'
            ? 'It takes about a minute with an authenticator app on your phone.'
            : 'Enter the current code from your authenticator app to continue.'}
        </p>

        {state === 'error' ? (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-danger" role="alert">
              We could not check your two-step verification. Check your connection and try again.
            </p>
            <Button
              className="self-start"
              onClick={() => {
                void status.refetch();
                void factors.refetch();
              }}
            >
              Try again
            </Button>
          </div>
        ) : state === 'verify' && verified[0] ? (
          <TotpCodePanel factorId={verified[0].id} />
        ) : (
          <TotpEnrolPanel />
        )}

        <div className="mt-6 flex flex-wrap gap-2 border-t border-border pt-4">
          {otherWorkspaces.map(option => (
            <Button
              key={option.workspace_id}
              variant="outline"
              size="sm"
              onClick={() => {
                void setActiveWorkspace(option.workspace_id);
              }}
            >
              Switch to {option.workspace.name}
            </Button>
          ))}
          <Button
            variant="ghost"
            size="sm"
            onClick={async () => {
              const { error } = await supabase.auth.signOut();
              if (error) log.error('Sign out from two-step gate failed', { error });
            }}
          >
            <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
            Sign out
          </Button>
        </div>
      </Card>
    </div>
  );
}
